import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Plus, Edit2, Trash2, Upload } from 'lucide-react';
import { apiRequest } from '../../utils/api';
import CountryCodeSelector from '../../components/CountryCodeSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ArtistsPage() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country_code: '+1',
    short_intro: '',
    profile_picture: null,
    banner_picture: null
  });
  const [profilePreview, setProfilePreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/artists`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setArtists(response.data.artists || []);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingArtist(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      country_code: '+1',
      short_intro: '',
      profile_picture: null,
      banner_picture: null
    });
    setProfilePreview(null);
    setBannerPreview(null);
    setShowModal(true);
  };

  const handleEdit = (artist) => {
    setEditingArtist(artist);
    
    // Parse country code from phone number
    let countryCode = '+1';
    let phoneNumber = artist.phone;
    
    if (artist.phone) {
      // Try to extract country code (assuming format: +XX... or +XXX...)
      const match = artist.phone.match(/^(\+\d{1,3})(.*)/);
      if (match) {
        countryCode = match[1];
        phoneNumber = match[2];
      }
    }
    
    setFormData({
      name: artist.name,
      email: artist.email,
      phone: phoneNumber,
      country_code: countryCode,
      short_intro: artist.short_intro || '',
      profile_picture: null,
      banner_picture: null
    });
    setProfilePreview(artist.profile_picture ? `${BACKEND_URL}${artist.profile_picture}` : null);
    setBannerPreview(artist.banner_picture ? `${BACKEND_URL}${artist.banner_picture}` : null);
    setShowModal(true);
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, [type]: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'profile_picture') {
          setProfilePreview(reader.result);
        } else {
          setBannerPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('admin_token');
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      // Combine country code and phone number
      const fullPhone = `${formData.country_code}${formData.phone}`;
      formDataToSend.append('phone', fullPhone);
      formDataToSend.append('short_intro', formData.short_intro);
      
      if (formData.profile_picture) {
        formDataToSend.append('profile_picture', formData.profile_picture);
      }
      if (formData.banner_picture) {
        formDataToSend.append('banner_picture', formData.banner_picture);
      }

      const url = editingArtist
        ? `${BACKEND_URL}/api/admin/artists/${editingArtist.id}`
        : `${BACKEND_URL}/api/admin/artists`;

      const response = await fetch(url, {
        method: editingArtist ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        setShowModal(false);
        fetchArtists();
      } else {
        alert('Failed to save artist');
      }
    } catch (err) {
      alert('Error saving artist');
    }
  };

  const handleDelete = async (artist) => {
    if (!window.confirm('Are you sure you want to delete this artist?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/artists/${artist.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchArtists();
      } else {
        alert('Failed to delete artist');
      }
    } catch (err) {
      alert('Error deleting artist');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-navy">Artists</h1>
          <Button onClick={handleAdd} className="bg-navy text-white">
            <Plus className="w-4 h-4 mr-2" />
            Add Artist
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {artists.map((artist) => (
              <div key={artist.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {artist.banner_picture && (
                  <img
                    src={`${BACKEND_URL}${artist.banner_picture}`}
                    alt="Banner"
                    className="w-full h-32 object-cover"
                  />
                )}
                
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    {artist.profile_picture ? (
                      <img
                        src={`${BACKEND_URL}${artist.profile_picture}`}
                        alt={artist.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold text-xl">
                        {artist.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-navy">{artist.name}</h3>
                      <p className="text-sm text-gray-600">{artist.email}</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2">{artist.phone}</p>
                  {artist.short_intro && (
                    <p className="text-sm text-gray-500 mb-4 line-clamp-2">{artist.short_intro}</p>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEdit(artist)}
                      variant="outline"
                      className="flex-1"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDelete(artist)}
                      variant="outline"
                      className="border-red-300 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-navy mb-6">
                {editingArtist ? 'Edit Artist' : 'Add Artist'}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-navy mb-2">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Phone *</label>
                  <div className="flex gap-2">
                    <div className="w-32">
                      <CountryCodeSelector
                        value={formData.country_code}
                        onChange={(code) => setFormData({ ...formData, country_code: code })}
                      />
                    </div>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="flex-1 px-4 py-3 rounded-lg border border-gray-300"
                      placeholder="Phone number"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Short Intro</label>
                  <textarea
                    value={formData.short_intro}
                    onChange={(e) => setFormData({ ...formData, short_intro: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Profile Picture</label>
                  {profilePreview && (
                    <img src={profilePreview} alt="Profile" className="w-24 h-24 rounded-full object-cover mb-2" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'profile_picture')}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-2">Banner Picture</label>
                  {bannerPreview && (
                    <img src={bannerPreview} alt="Banner" className="w-full h-32 object-cover rounded-lg mb-2" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'banner_picture')}
                    className="w-full"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={() => setShowModal(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-navy text-white"
                  >
                    {editingArtist ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
